import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import { useState } from 'react';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_USERGROUP_USER } from '@sorare/core/src/constants/routes';
import { isType } from '@sorare/core/src/gql';
import { useCurrentSport } from '@sorare/core/src/hooks/useCurrentSport';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { CumulativeRanking } from '../CumulativeRankRewards/Ranking';
import {
  CumulativePaginatedRankingsQuery,
  CumulativePaginatedRankingsQueryVariables,
} from './__generated__/index.graphql';

const CUMULATIVE_PAGINATED_RANKINGS_QUERY = gql`
  query CumulativePaginatedRankingsQuery(
    $page: Int
    $pageSize: Int!
    $userGroupSlug: String!
  ) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        myMembership {
          id
        }
        membershipsPaginated(page: $page, pageSize: $pageSize) {
          totalCount
          currentPage
          nodes {
            id
            ... on So5UserGroupMembership {
              id
              ...CumulativeRanking_So5UserGroupMembership
            }
          }
        }
      }
    }
  }
  ${CumulativeRanking.fragments.So5UserGroupMembership}
` as TypedDocumentNode<
  CumulativePaginatedRankingsQuery,
  CumulativePaginatedRankingsQueryVariables
>;

const PAGE_SIZE = 20;

type Props = {
  userGroupSlug: string;
};

export const CumulativePaginatedRankings = ({ userGroupSlug }: Props) => {
  const [page, setPage] = useState<number>();
  const sport = useCurrentSport();

  const [query, { data, loading, called }] = useLazyQuery(
    CUMULATIVE_PAGINATED_RANKINGS_QUERY
  );

  if (!called) {
    query({
      variables: {
        page: 0,
        userGroupSlug,
        pageSize: PAGE_SIZE,
      },
    });
  }

  const { membershipsPaginated, myMembership } = data?.so5.so5UserGroup || {};
  const { currentPage, totalCount } = membershipsPaginated || {};

  return (
    <Vertical>
      <div>
        {loading && <LoadingIndicator fullHeight />}
        {membershipsPaginated?.nodes?.map(
          userMembership =>
            isType(userMembership, 'So5UserGroupMembership') && (
              <CumulativeRanking
                key={userMembership.id}
                so5Ranking={userMembership}
                userGroupSlug={userGroupSlug}
                isMyRanking={userMembership.id === myMembership?.id}
                rankingLink={(
                  fixtureSlug,
                  groupSlug,
                  userSlug,
                  eventType = So5FixtureEvent.CLASSIC
                ) => {
                  return generateSportPath(
                    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_USERGROUP_USER,
                    {
                      sport,
                      params: {
                        eventType: eventType.toLowerCase(),
                        fixture: fixtureSlug,
                        userGroup: groupSlug,
                        user: userSlug,
                      },
                    }
                  );
                }}
              />
            )
        )}
      </div>
      <Pagination
        currentPage={page || currentPage || 0}
        pages={Math.ceil((totalCount || 0) / PAGE_SIZE)}
        onSelect={p => {
          setPage(p);
          query({
            variables: {
              page: p,
              userGroupSlug,
              pageSize: PAGE_SIZE,
            },
          });
        }}
      />
    </Vertical>
  );
};
